// "Next up": the badges a member is closest to, from GET /api/badges/me/next.
// On the profile (under the showcase), at the top of /badges and, compact, on the
// home. Each row is the badge still locked, the server's line ("2 more coffees")
// and a progress bar; a tap opens the badge in the collection. Draws nothing
// while it loads, when the request fails or when there is nothing to go after.
import React, { useId } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import { BadgeArt } from "../art";
import { collectionPath } from "../reveal/revealQueue";
import GoalBar from "./GoalBar";
import { goalCount, goalLabel, goalLine, visibleGoals } from "./goalFormat";
import { useGetMyNextBadgesQuery } from "./goalsApi";
import "./nextBadges.css";

export type NextBadgesVariant = "full" | "compact";

export interface NextBadgesProps {
  /** full: the profile and /badges, with the badge descriptions. compact: the home, tighter rows. */
  variant?: NextBadgesVariant;
  title?: string;
  /** Open a goal's badge. Defaults to the collection with that badge open. */
  onOpen?: (code: string) => void;
  className?: string;
}

const ART_PX: Record<NextBadgesVariant, number> = { full: 44, compact: 34 };

const NextBadges: React.FC<NextBadgesProps> = ({ variant = "full", title = "Next up", onOpen, className }) => {
  const auth = useAppSelector((s) => s.auth);
  const ready = auth.bootstrapStatus === "ready" && !!auth.token;
  const navigate = useNavigate();
  const headingId = useId();
  const { data, isLoading, isError } = useGetMyNextBadgesQuery(undefined, { skip: !ready });
  const goals = visibleGoals(data?.goals);

  if (!ready || isLoading || isError || !goals.length) return null;

  const open = (code: string) => {
    if (onOpen) onOpen(code);
    else navigate(collectionPath(code));
  };
  const compact = variant === "compact";
  const classes = ["bnext", `bnext--${variant}`, className].filter(Boolean).join(" ");

  return (
    <section className={classes} aria-labelledby={headingId} data-testid="next-badges">
      <h2 id={headingId} className="bnext__title">
        {title}
      </h2>
      <ul className="bnext__list">
        {goals.map((goal) => {
          const next = goal.next;
          return (
            <li key={`${goal.category}:${next.code}`} className="bnext__item">
              <button
                type="button"
                className="bnext__row"
                data-code={next.code}
                aria-label={goalLabel(goal)}
                onClick={() => open(next.code)}
              >
                <span className="bnext__art">
                  <BadgeArt
                    category={next.category}
                    tier={next.tier}
                    number={next.number}
                    code={next.code}
                    name={next.name}
                    size={ART_PX[variant]}
                    variant="mini"
                    locked
                  />
                </span>
                <span className="bnext__body">
                  <span className="bnext__name">{next.name}</span>
                  <span className="bnext__line">{goalLine(goal)}</span>
                  {!compact && next.description && <span className="bnext__desc">{next.description}</span>}
                  <span className="bnext__progress">
                    <GoalBar goal={goal} />
                    <span className="bnext__count">{goalCount(goal)}</span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default NextBadges;
